/**
 * Earnings API Service
 * Handles Stripe Connect and earnings/payout endpoints.
 */

import { apiClient } from "@/lib/api-client"

interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export interface ConnectStatus {
  connected: boolean
  accountId: string | null
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted?: boolean
  accountCountry?: string | null
  platformCountry?: string | null
  destinationChargeReady?: boolean
  issues?: string[]
}

export interface EarningsSummary {
  totalIncome: number
  pendingIncome: number
  availableForPayout: number
  queuedForPayout: number
  pendingAtStripe: number
  inTransit: number
  paidOut: number
  currency: string
  lastPayout?: {
    id: string
    status: string
    nextAttemptAt: string | null
    failureReason: string | null
    stripePayoutId: string | null
  } | null
}

export interface Transaction {
  id: string
  ritualId?: string
  type: "income" | "payout"
  description: string
  status: string
  date: string
  earned: number
  fee: number
  sent: number
  currency: string
  payoutStatus?: string | null
  payoutId?: string | null
  fundingModel?: string | null
  stripeChargeId?: string | null
  stripeTransferId?: string | null
  stripePayoutId?: string | null
  nextAttemptAt?: string | null
  failureCode?: string | null
  failureReason?: string | null
  title?: string | null
  sales?: number
  amount?: number
  format?: string
  buyerName?: string
  buyerId?: string
  platformFee?: number
  creatorNetAmount?: number
}

export interface TransactionsResponse {
  rows: Transaction[]
  total: number
  page: number
  limit: number
}

export interface PayoutResult {
  payoutId: string
  amount: number
  fee: number
  netAmount: number
  status: string
  queueStatus: string
  nextAttemptAt: string | null
  stripeTransferId?: string | null
  stripePayoutId?: string | null
}

export async function getConnectStatus(): Promise<ConnectStatus> {
  const res = await apiClient.get<ApiResponse<ConnectStatus>>("/api/earnings/connect/status")
  return res.data.data
}

export async function createConnectAccount(
  returnUrl: string,
  refreshUrl: string,
): Promise<{ accountId: string; url: string }> {
  const res = await apiClient.post<ApiResponse<{ accountId: string; url: string }>>(
    "/api/earnings/connect",
    { returnUrl, refreshUrl },
  )
  return res.data.data
}

export async function getDashboardLink(): Promise<{ url: string }> {
  const res = await apiClient.post<ApiResponse<{ url: string }>>("/api/earnings/connect/dashboard")
  return res.data.data
}

export async function disconnectAccount(): Promise<void> {
  await apiClient.post("/api/earnings/connect/disconnect")
}

export async function getEarningsSummary(): Promise<EarningsSummary> {
  const res = await apiClient.get<ApiResponse<EarningsSummary>>("/api/earnings/summary")
  return res.data.data
}

export async function getTransactions(
  tab: string,
  page: number,
  limit: number,
  search?: string,
): Promise<TransactionsResponse> {
  const params = new URLSearchParams({
    tab,
    page: String(page),
    limit: String(limit),
  })
  if (search) {
    params.set("search", search)
  }
  const res = await apiClient.get<ApiResponse<TransactionsResponse>>(
    `/api/earnings/transactions?${params.toString()}`,
  )
  return res.data.data
}

export async function requestPayout(): Promise<PayoutResult> {
  const res = await apiClient.post<ApiResponse<PayoutResult>>("/api/earnings/payout")
  return res.data.data
}

export async function getTransactionById(transactionId: string): Promise<Transaction> {
  const res = await apiClient.get<ApiResponse<Transaction>>(
    `/api/earnings/transactions/${transactionId}`,
  )
  return res.data.data
}

export async function getRelatedTransactions(transactionId: string): Promise<Transaction[]> {
  const res = await apiClient.get<ApiResponse<Transaction[]>>(
    `/api/earnings/transactions/${transactionId}/related`,
  )
  return res.data.data
}

export async function refundTransaction(transactionId: string): Promise<{ refundId: string }> {
  const res = await apiClient.post<ApiResponse<{ refundId: string }>>(
    `/api/earnings/transactions/${transactionId}/refund`,
  )
  return res.data.data
}

export async function getPayoutReceipt(stripePayoutId: string): Promise<unknown> {
  const res = await apiClient.get<ApiResponse<unknown>>(
    `/api/earnings/payouts/${stripePayoutId}/receipt`,
  )
  return res.data.data
}
